import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Form, Input, Switch, Tooltip } from 'antd'
import type React from 'react'
import { useState } from 'react'
import type { GridApi, RowNode } from 'ag-grid-community'
import { IconArrowRight, IconCircleOff } from '@tabler/icons-react'
import { updateForecastValue } from './columnDefs'

enum EditorOptions {
  Percent = 'Percent',
  Amount = 'Amount',
  Remove = 'Remove',
}

function BarEditor({ selectedOption, setSelectedOption, inputValue, setInputValue, isExcluded }) {
  const isInputRequired = !isExcluded && selectedOption !== EditorOptions.Remove
  const handleOptionClick = (option: EditorOptions) => {
    setSelectedOption(option)
    if (option === EditorOptions.Remove) {
      setInputValue('') // Clear input when "Remove" is selected
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10)
    if (!isNaN(value) && value >= 0) {
      setInputValue(value)
    } else {
      setInputValue('') // Clear the input if the value is not a positive integer
    }
  }

  return (
    <Horizontal flex={1} style={{ height: '60px' }} verticalCenter className='bg-1 px-4  border-bottom border-top'>
      <EditorOptionTooltip values={[10.0, 150, 165, -10, 150, 135]}>
        <GraviButton
          buttonText={EditorOptions.Percent}
          onClick={() => handleOptionClick(EditorOptions.Percent)}
          theme2={selectedOption === EditorOptions.Percent}
          className='square-ish'
        />
      </EditorOptionTooltip>
      <Tooltip title='Overrides forecast value to amount'>
        <GraviButton
          buttonText={EditorOptions.Amount}
          onClick={() => handleOptionClick(EditorOptions.Amount)}
          theme2={selectedOption === EditorOptions.Amount}
          className='square-ish'
        />
      </Tooltip>
      <Tooltip title='Removes any existing overrides'>
        <GraviButton
          buttonText={EditorOptions.Remove}
          onClick={() => handleOptionClick(EditorOptions.Remove)}
          theme2={selectedOption === EditorOptions.Remove}
          className='square-ish'
        />
      </Tooltip>
      <Form.Item
        name='inputValue'
        rules={[
          { required: isInputRequired, message: 'Input is required.' },
          {
            validator: (_, value) => {
              if (selectedOption === EditorOptions.Percent) {
                if (value < -100 || value > 1000) {
                  return Promise.reject(new Error('Invalid Percentage'))
                }
              } else if (selectedOption === EditorOptions.Amount) {
                if (value < 0 || value > 1000000) {
                  return Promise.reject(new Error('Invalid Amount'))
                }
              }
              return Promise.resolve() // If validation passes
            },
          },
        ]}
        label='Value'
        style={{ marginLeft: isInputRequired ? '8px' : '17px' }}
      >
        <Input
          type='number'
          value={inputValue}
          onChange={handleInputChange}
          disabled={selectedOption === EditorOptions.Remove}
          placeholder={
            selectedOption === EditorOptions.Percent
              ? 'Enter Percent'
              : selectedOption === EditorOptions.Amount
                ? 'Enter Amount'
                : ''
          }
          style={{ width: '125px' }}
        />
      </Form.Item>
    </Horizontal>
  )
}

function EditorOptionTooltip({ children, values }) {
  return (
    <Tooltip
      title={
        <Vertical>
          <Horizontal verticalCenter>
            <span className='mr-3'>Value {values[0]}.0</span>
            <s className='mr-1 ml-1'>{values[1]}</s>
            <IconArrowRight className='override-icon' />
            {values[2]}
          </Horizontal>
          <Horizontal verticalCenter>
            <span className='mr-3'>Value {values[3]}.0</span>
            <s className='mr-1'>{values[4]}</s>
            <IconArrowRight className='override-icon' />
            {values[5]}
          </Horizontal>
        </Vertical>
      }
    >
      {children}
    </Tooltip>
  )
}

function DirtyCounter({ selectedCells }) {
  return (
    <Horizontal verticalCenter className='dirty-counter bg-1 px-4 py-2 mb-3 mt-3 border-bottom border-top border-right'>
      <Texto align='left'>{selectedCells?.length || 0} Items Will Be Updated</Texto>
    </Horizontal>
  )
}

function BarSubmission({
  handleClose,
  selectedCells,
  form,
  isExcludedFromForecast,
  setIsExcludedFromForecast,
  selectedOption,
}) {
  return (
    <Horizontal
      flex={0}
      justifyContent='flex-end'
      verticalCenter
      className='bg-1 px-4 py-2 mb-3 mt-3 border-bottom border-top border-right'
      style={{ borderRadius: '0 3px 3px 0', height: '60px' }}
    >
      <Form.Item name='excludeValue'>
        <Tooltip title='Excludes Forecast For This Tank On These Days'>
          <span>
            <Horizontal style={{ gap: '.5em', alignItems: 'center', height: '100%' }}>
              <Texto style={{ lineHeight: '1.5', fontWeight: 600 }}>Exclude From:</Texto>
              <IconCircleOff className='override-icon' />
              <Texto>Forecast</Texto>
              <Switch
                size='small'
                className='mr-1'
                checked={isExcludedFromForecast}
                onChange={(value) => setIsExcludedFromForecast(value)}
                disabled={selectedOption === EditorOptions.Remove}
              />
            </Horizontal>
          </span>
        </Tooltip>
      </Form.Item>
      <GraviButton
        buttonText='Cancel'
        onClick={() => {
          handleClose()
        }}
        className='square-ish mr-2'
      />
      <Tooltip title='Select cells to update'>
        <GraviButton
          onClick={() => selectedCells?.length && form.submit()}
          theme2
          buttonText='Preview'
          className='square-ish mr-4'
          style={
            !selectedCells?.length
              ? {
                  backgroundColor: 'var(--theme-color-1-dim',
                  color: '#fff',
                  cursor: 'not-allowed',
                }
              : {}
          }
        />
      </Tooltip>
    </Horizontal>
  )
}

async function stageBulkChange(
  selectedCells,
  selectedValue: number,
  selectedOption: EditorOptions,
  gridAPIRef: { current: GridApi },
  dirtyRef,
  isExcludedFromForecast: boolean
) {
  // Loops through the selected cells, changes the volume like it was changed through the grid
  const itemsToUpdateMap = new Map()
  for (const cell of selectedCells) {
    const rowNode: RowNode = gridAPIRef.current.getRowNode(cell.id)
    const original_volume = cell.original_volume ?? cell.volume
    let newVolume
    if (isExcludedFromForecast && !selectedValue && selectedOption !== EditorOptions.Remove) {
      newVolume = cell.volume
    } else if (selectedOption === EditorOptions.Amount) {
      newVolume = selectedValue
    } else if (selectedOption === EditorOptions.Percent) {
      newVolume = Math.round(original_volume * (1 + selectedValue / 100))
    } else if (selectedOption === EditorOptions.Remove) {
      newVolume = null
    } else {
      newVolume = cell.original_volume
    }
    updateForecastValue(rowNode, newVolume, cell.forecast_index, isExcludedFromForecast)
    const itemToUpdate = itemsToUpdateMap.get(rowNode.rowIndex) ?? { ...rowNode.data }
    itemsToUpdateMap.set(rowNode.rowIndex, itemToUpdate)
  }
  for (const [index, item] of itemsToUpdateMap) {
    await dirtyRef.current.updateDirtyRow(item, { updatedRowIndex: index })
  }
  gridAPIRef.current.applyTransaction({ update: Array.from(itemsToUpdateMap.values()) })
}

export function ForecastBulkChangeBar({
  selectedCells,
  setSelectedCells,
  gridAPIRef,
  dirtyRef,
  isExcludedFromForecast,
  setExcludeFromForecast,
}) {
  const [form] = Form.useForm()

  const [selectedOption, setSelectedOption] = useState(EditorOptions.Percent)
  const [inputValue, setInputValue] = useState<number | string>('')

  const handleCancel = () => {
    form.resetFields()
    setSelectedCells(null)
    gridAPIRef.current.clearRangeSelection()
    setExcludeFromForecast(false)
  }

  const onFinish = (values) => {
    stageBulkChange(selectedCells, values.inputValue, selectedOption, gridAPIRef, dirtyRef, isExcludedFromForecast)
    form.resetFields()
    setSelectedCells(null)
    gridAPIRef.current.clearRangeSelection()
    setExcludeFromForecast(false)
  }

  return (
    <Form form={form} onFinish={onFinish} layout='inline'>
      <Horizontal className='full-height-width ' verticalCenter alignItems='space-between'>
        <Horizontal
          horizontalCenter
          verticalCenter
          className='update-forecasts-text px-4 py-3 mb-3 mt-3 border-bottom border-top border-left'
        >
          <Texto appearance='white' category='h5'>
            UPDATE <span style={{ fontWeight: 900 }}>FORECASTS</span>
          </Texto>
        </Horizontal>
        <DirtyCounter selectedCells={selectedCells} />
        <BarEditor
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          inputValue={inputValue}
          setInputValue={setInputValue}
          isExcluded={isExcludedFromForecast}
        />
        <BarSubmission
          handleClose={handleCancel}
          selectedCells={selectedCells}
          form={form}
          isExcludedFromForecast={isExcludedFromForecast}
          setIsExcludedFromForecast={setExcludeFromForecast}
          selectedOption={selectedOption}
        />
      </Horizontal>
    </Form>
  )
}
